import { createApi } from '@reduxjs/toolkit/query/react';
import { InvitedBy } from '@clareefai/types';
import { baseQueryWithReauth } from './api';

export interface TeamMemberStats {
  totalProspects: number;
  activeProspects: number;
  completedProspects: number;
  convertedProspects: number;
  totalMeetings: number;
}

export interface TeamMemberResponse {
  id: string;
  vendorId: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone?: string | null;
  role: string;
  avatar: string;
  status: string;
  assignedProspects: string[];
  assignedPromoters: string[];
  stats: TeamMemberStats | null;
  joinedDate: string;
  createdAt: string;
  companyName?: string | null;
  companyLogo?: string | null;
  /** Whether this team member has ever logged in / completed onboarding (first-login chip). */
  hasLoggedIn?: boolean;
  /** Who invited/created this team member (vendor or team member). */
  invitedBy?: InvitedBy | null;
  /** How many "join" reminders have been sent to this still-PENDING member. */
  reminderCount?: number;
  /** ISO timestamp of the last join reminder, if any. */
  lastRemindedAt?: string | null;
}

export interface AssignedPromoterSummary {
  id: string;
  fullName: string;
  email: string;
}

export interface TeamMemberAssignedProspect {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string | null;
  status: string;
  createdAt?: string | null;
}

export interface TeamMemberDetailResponse {
  teamMember: TeamMemberResponse;
  assignedProspects: {
    active: TeamMemberAssignedProspect[];
    completed: TeamMemberAssignedProspect[];
    total: number;
  };
  assignedPromoters: AssignedPromoterSummary[];
}

export interface CreateTeamMemberRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'Account Executive' | 'Customer Success' | 'Account Manager' | 'Manager' | 'Marketing Manager';
}

export interface UpdateTeamMemberRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: 'Account Executive' | 'Customer Success' | 'Account Manager' | 'Manager' | 'Marketing Manager';
  email?: string;
}

export interface AssignProspectsRequest {
  prospectIds: string[];
}

export interface AssignPromotersRequest {
  promoterIds: string[];
}

export const teamMemberApi = createApi({
  reducerPath: 'teamMemberApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TeamMember'],
  endpoints: (builder) => ({
    // Get all team members
    getTeamMembers: builder.query<TeamMemberResponse[], void>({
      query: () => '/vendors/me/team-members',
      providesTags: ['TeamMember'],
    }),
    
    // Get team member by ID
    getTeamMemberById: builder.query<TeamMemberDetailResponse, string>({
      query: (id) => `/vendors/me/team-members/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'TeamMember', id }],
    }),
    
    // Create team member
    createTeamMember: builder.mutation<TeamMemberResponse, CreateTeamMemberRequest>({
      query: (body) => ({
        url: '/vendors/me/team-members',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamMember'],
    }),
    
    // Update team member
    updateTeamMember: builder.mutation<TeamMemberResponse, { id: string; data: UpdateTeamMemberRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/team-members/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),
    
    // Deactivate team member
    deactivateTeamMember: builder.mutation<TeamMemberResponse, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamMember'],
    }),
    
    // Assign prospects to team member
    assignProspects: builder.mutation<TeamMemberResponse, { id: string; data: AssignProspectsRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/team-members/${id}/assign-prospects`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),
    
    // Unassign prospects from team member
    unassignProspects: builder.mutation<TeamMemberResponse, { id: string; data: AssignProspectsRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/team-members/${id}/unassign-prospects`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),
    
    // Assign promoters to team member
    assignPromoters: builder.mutation<TeamMemberResponse, { id: string; data: AssignPromotersRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/team-members/${id}/assign-promoters`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),
    
    // Unassign promoters from team member
    unassignPromoters: builder.mutation<TeamMemberResponse, { id: string; data: AssignPromotersRequest }>({
      query: ({ id, data }) => ({
        url: `/vendors/me/team-members/${id}/unassign-promoters`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),

    // Resend invitation to a PENDING team member
    resendInvitation: builder.mutation<TeamMemberResponse, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}/resend-invitation`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),

    // Remind a PENDING team member to join (softer nudge; rotates the magic link)
    remindTeamMember: builder.mutation<TeamMemberResponse, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}/remind`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),

    // Reset password of an ACTIVE team member (sends them a reset link)
    resetTeamMemberPassword: builder.mutation<TeamMemberResponse, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}/reset-password`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),

    // Reactivate an INACTIVE team member
    reactivateTeamMember: builder.mutation<TeamMemberResponse, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'TeamMember', id }, 'TeamMember'],
    }),

    // Permanently delete a team member
    deleteTeamMember: builder.mutation<void, string>({
      query: (id) => ({
        url: `/vendors/me/team-members/${id}/permanent`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamMember'],
    }),
    
    // Team member self-service: Get own profile
    getMyProfile: builder.query<TeamMemberResponse, void>({
      query: () => '/team-members/me',
      providesTags: ['TeamMember'],
    }),
    
    // Team member self-service: Update own profile
    updateMyProfile: builder.mutation<TeamMemberResponse, UpdateTeamMemberRequest>({
      query: (body) => ({
        url: '/team-members/me',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TeamMember'],
    }),
    
    // Team member self-service: Get assigned prospects
    getMyAssignedProspects: builder.query<any[], { status?: string }>({
      query: ({ status }) => ({
        url: '/team-members/me/prospects',
        params: status ? { status } : undefined,
      }),
      providesTags: ['TeamMember'],
    }),

    // Team member self-service: Get assigned promoters (DEV-77)
    getMyAssignedPromoters: builder.query<any[], void>({
      query: () => '/team-members/me/promoters',
      providesTags: ['TeamMember'],
    }),
  }),
});

export const {
  useGetTeamMembersQuery,
  useGetTeamMemberByIdQuery,
  useCreateTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useDeactivateTeamMemberMutation,
  useAssignProspectsMutation,
  useUnassignProspectsMutation,
  useAssignPromotersMutation,
  useUnassignPromotersMutation,
  useResendInvitationMutation,
  useRemindTeamMemberMutation,
  useResetTeamMemberPasswordMutation,
  useReactivateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useGetMyProfileQuery,
  useUpdateMyProfileMutation,
  useGetMyAssignedProspectsQuery,
  useGetMyAssignedPromotersQuery,
} = teamMemberApi;
